import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  ArrowRight,
  Tag,
  Truck,
  Shield,
  X,
  Heart,
  ChevronRight,
  Package,
  Clock,
  RotateCcw,
  Sparkles,
  ShoppingBag,
  Home,
  TrendingUp
} from 'lucide-react';
import { formatPrice } from '../lib/utils';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart, clearCart, loading } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [savedForLater, setSavedForLater] = useState([]);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch recommended products
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products?limit=6`);
        // Filter out items already in cart
        const cartProductIds = cart.items?.map(item => item.product_id) || [];
        const filtered = response.data.filter(p => !cartProductIds.includes(p._id));
        setRecommendedProducts(filtered.slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
      }
    };
    fetchRecommendations();
  }, [API_URL, cart.items]);

  const subtotal = cart.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
  const itemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const couponDiscount = appliedCoupon ? 
    (appliedCoupon.discount_type === 'percentage' ? 
      (subtotal * appliedCoupon.discount_value / 100) : 
      appliedCoupon.discount_value) : 0;
  const estimatedShipping = subtotal >= 500 ? 0 : 72; // Free shipping above ₹500
  const total = subtotal - couponDiscount + estimatedShipping;

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    await updateQuantity(productId, newQuantity);
  };

  const handleRemoveItem = async (productId) => {
    await removeFromCart(productId);
    toast.success('Item removed from cart');
  };

  const handleSaveForLater = (item) => {
    setSavedForLater(prev => [...prev, item]);
    removeFromCart(item.product_id || item.id);
    toast.success('Item saved for later');
  };

  const handleMoveToCart = async (item) => {
    // Add back to cart logic here
    setSavedForLater(prev => prev.filter(i => i.product_id !== item.product_id));
    toast.success('Item moved to cart');
  };

  const handleClearCart = async () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      await clearCart();
      toast.success('Cart cleared');
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    setCouponLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (couponCode.toUpperCase() === 'WELCOME10') {
        setAppliedCoupon({ code: 'WELCOME10', discount_type: 'percentage', discount_value: 10 });
        toast.success('10% discount applied!');
      } else if (couponCode.toUpperCase() === 'NAMMAHUT50') {
        setAppliedCoupon({ code: 'NAMMAHUT50', discount_type: 'fixed', discount_value: 50 });
        toast.success('₹50 discount applied!');
      } else {
        toast.error('Invalid coupon code');
      }
    } catch (error) {
      toast.error('Failed to apply coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Empty Cart State - Enhanced Design
  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Empty Cart Hero */}
          <div className="text-center py-16">
            <div className="relative inline-block mb-8">
              <div className="w-32 h-32 bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] rounded-full flex items-center justify-center mx-auto">
                <ShoppingCart className="w-16 h-16 text-[#0F5132]" />
              </div>
              <div className="absolute -right-2 -top-2 w-12 h-12 bg-[#FFD700] rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#0F5132]" />
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Your cart is empty
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
              Looks like you have not added any items yet. Explore our collection of traditional products!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/products">
                <Button className="bg-[#0F5132] hover:bg-[#0d4429] text-white px-8 py-3 text-lg" data-testid="start-shopping-button">
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Start Shopping
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="px-8 py-3 text-lg border-[#0F5132] text-[#0F5132] hover:bg-[#E8F5E9]">
                  <Home className="w-5 h-5 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>

          {/* Quick Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
              <div className="w-12 h-12 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-6 h-6 text-[#0F5132]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Free Shipping</h3>
              <p className="text-sm text-gray-600">On orders above ₹500</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
              <div className="w-12 h-12 bg-[#FFF3E0] rounded-full flex items-center justify-center mx-auto mb-4">
                <RotateCcw className="w-6 h-6 text-[#E65100]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Easy Returns</h3>
              <p className="text-sm text-gray-600">7-day return policy</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
              <div className="w-12 h-12 bg-[#E3F2FD] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-[#1565C0]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Secure Payment</h3>
              <p className="text-sm text-gray-600">100% secure checkout</p>
            </div>
          </div>

          {/* Recommended Products */}
          {recommendedProducts.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-6 h-6 text-[#0F5132]" />
                <h2 className="text-2xl font-bold text-gray-900">Popular Products</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {recommendedProducts.map((product) => (
                  <Link
                    key={product._id}
                    to={`/products/${product._id}`}
                    className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={product.images?.[0] || 'https://via.placeholder.com/300'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-2">
                        {product.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#0F5132]">{formatPrice(product.sale_price || product.price)}</span>
                        {product.sale_price && product.sale_price < product.price && (
                          <span className="text-xs text-gray-400 line-through">{formatPrice(product.price)}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Cart with Items
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b sticky top-0 z-10 md:relative">
        <div className="max-w-7xl mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Shopping Cart</h1>
              <p className="text-sm text-gray-600 mt-1">
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleClearCart}
              className="text-red-600 hover:bg-red-50"
              data-testid="clear-cart-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Clear Cart</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Cart Items Column */}
          <div className="flex-1 space-y-4">
            {/* Checkout Button - Top (Mobile) */}
            <div className="lg:hidden">
              <Button 
                onClick={handleCheckout}
                className="w-full bg-[#FFD700] hover:bg-[#FFC000] text-[#0F5132] font-bold py-4 text-lg rounded-xl shadow-lg"
                data-testid="checkout-button-top"
              >
                Proceed to Checkout ({itemCount} items)
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Cart Items */}
            {cart.items.map((item, index) => (
              <Card key={item.product_id || index} className="overflow-hidden bg-white shadow-sm">
                <CardContent className="p-4 md:p-6">
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <Link to={`/products/${item.product_id}`} className="flex-shrink-0">
                      <img 
                        src={item.image || 'https://via.placeholder.com/120'} 
                        alt={item.name}
                        className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-lg border border-gray-100"
                        data-testid={`cart-item-image-${item.product_id}`}
                      />
                    </Link>
                    
                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                        <div className="flex-1">
                          <Link 
                            to={`/products/${item.product_id}`}
                            className="font-semibold text-gray-900 hover:text-[#0F5132] transition-colors line-clamp-2 text-base md:text-lg"
                            data-testid={`cart-item-name-${item.product_id}`}
                          >
                            {item.name}
                          </Link>
                          
                          {/* Variant Info */}
                          {item.variant && (
                            <p className="text-sm text-gray-500 mt-1">
                              Variant: {item.variant}
                            </p>
                          )}
                          
                          {/* Stock Status */}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                              In Stock
                            </span>
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Truck className="w-3 h-3" />
                              Est. delivery: 5-7 days
                            </span>
                          </div>
                        </div>
                        
                        {/* Price - Desktop */}
                        <div className="hidden md:block text-right">
                          <p className="text-xl font-bold text-gray-900">
                            {formatPrice(item.price * item.quantity)}
                          </p>
                          {item.quantity > 1 && (
                            <p className="text-sm text-gray-500">
                              {formatPrice(item.price)} each
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions Row */}
                      <div className="flex items-center justify-between mt-4 gap-4">
                        {/* Quantity Controls */}
                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => handleQuantityChange(item.product_id || item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            data-testid={`decrease-quantity-${item.product_id || item.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-12 h-10 flex items-center justify-center font-semibold border-x border-gray-200">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleQuantityChange(item.product_id || item.id, item.quantity + 1)}
                            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors"
                            data-testid={`increase-quantity-${item.product_id || item.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Price - Mobile */}
                        <div className="md:hidden">
                          <p className="text-lg font-bold text-gray-900">
                            {formatPrice(item.price * item.quantity)}
                          </p>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="hidden sm:flex items-center gap-2">
                          <button
                            onClick={() => handleSaveForLater(item)}
                            className="text-sm text-gray-600 hover:text-[#0F5132] flex items-center gap-1 transition-colors"
                          >
                            <Heart className="w-4 h-4" />
                            Save for later
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => handleRemoveItem(item.product_id || item.id)}
                            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1 transition-colors"
                            data-testid={`remove-item-${item.product_id || item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </button>
                        </div>
                        
                        {/* Mobile Remove */}
                        <button
                          onClick={() => handleRemoveItem(item.product_id || item.id)}
                          className="sm:hidden p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Saved for Later Section */}
            {savedForLater.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Saved for Later ({savedForLater.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {savedForLater.map((item) => (
                    <Card key={item.product_id} className="overflow-hidden bg-white">
                      <CardContent className="p-4 flex gap-4">
                        <img 
                          src={item.image || 'https://via.placeholder.com/80'} 
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 line-clamp-1">{item.name}</h4>
                          <p className="font-bold text-[#0F5132] mt-1">{formatPrice(item.price)}</p>
                          <button
                            onClick={() => handleMoveToCart(item)}
                            className="text-sm text-[#0F5132] hover:underline mt-2"
                          >
                            Move to Cart
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Cross-sell Section */}
            {recommendedProducts.length > 0 && (
              <div className="mt-8 pt-8 border-t">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#FFD700]" />
                    Customers Also Bought
                  </h3>
                  <Link to="/products" className="text-sm text-[#0F5132] hover:underline flex items-center gap-1">
                    View All <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {recommendedProducts.slice(0, 4).map((product) => (
                    <Link
                      key={product._id}
                      to={`/products/${product._id}`}
                      className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-md transition-all group"
                    >
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={product.images?.[0] || 'https://via.placeholder.com/200'}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-gray-900 line-clamp-2">{product.name}</h4>
                        <p className="font-bold text-[#0F5132] mt-1">{formatPrice(product.sale_price || product.price)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar - Sticky */}
          <div className="lg:w-[380px] flex-shrink-0">
            <div className="lg:sticky lg:top-4">
              <Card className="bg-white shadow-lg overflow-hidden">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Order Summary
                  </h2>
                  
                  {/* Coupon Code */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Have a coupon?
                    </label>
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-green-700">{appliedCoupon.code}</span>
                          <span className="text-sm text-green-600">
                            ({appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}% off` : `₹${appliedCoupon.discount_value} off`})
                          </span>
                        </div>
                        <button onClick={handleRemoveCoupon} className="text-green-600 hover:text-green-700">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Enter coupon code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="flex-1"
                          data-testid="coupon-input"
                        />
                        <Button 
                          variant="outline" 
                          onClick={handleApplyCoupon}
                          disabled={couponLoading}
                          className="border-[#0F5132] text-[#0F5132] hover:bg-[#E8F5E9]"
                          data-testid="apply-coupon-button"
                        >
                          {couponLoading ? <LoadingSpinner size="sm" /> : 'Apply'}
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">Try: WELCOME10 or NAMMAHUT50</p>
                  </div>

                  {/* Price Breakdown */}
                  <div className="space-y-3 py-4 border-y border-gray-100">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal ({itemCount} items)</span>
                      <span className="font-medium" data-testid="subtotal">{formatPrice(subtotal)}</span>
                    </div>
                    
                    {appliedCoupon && (
                      <div className="flex justify-between text-green-600">
                        <span>Coupon Discount</span>
                        <span className="font-medium" data-testid="coupon-discount">-{formatPrice(couponDiscount)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-gray-600">
                      <span className="flex items-center gap-1">
                        Shipping
                        {estimatedShipping === 0 && <span className="text-xs text-green-600">(Free!)</span>}
                      </span>
                      <span className="font-medium">
                        {estimatedShipping === 0 ? (
                          <span className="text-green-600">FREE</span>
                        ) : (
                          formatPrice(estimatedShipping)
                        )}
                      </span>
                    </div>
                  </div>
                  
                  {/* Total */}
                  <div className="flex justify-between items-center py-4">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-[#0F5132]" data-testid="total">
                      {formatPrice(total)}
                    </span>
                  </div>
                  
                  {/* Free Shipping Progress */}
                  {subtotal < 500 && (
                    <div className="mb-4 p-3 bg-[#FFF8E1] border border-[#FFE082] rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="w-4 h-4 text-[#F57C00]" />
                        <span className="text-sm font-medium text-[#E65100]">
                          Add {formatPrice(500 - subtotal)} more for FREE shipping!
                        </span>
                      </div>
                      <div className="w-full bg-[#FFE082] rounded-full h-2">
                        <div 
                          className="bg-[#FF9800] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min((subtotal / 500) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Checkout Button */}
                  <Button 
                    onClick={handleCheckout}
                    className="w-full bg-[#FFD700] hover:bg-[#FFC000] text-[#0F5132] font-bold py-4 text-lg rounded-xl shadow-md hover:shadow-lg transition-all"
                    data-testid="checkout-button"
                  >
                    Proceed to Checkout
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  {/* Continue Shopping */}
                  <Link to="/products" className="block mt-3">
                    <Button variant="outline" className="w-full border-gray-200">
                      Continue Shopping
                    </Button>
                  </Link>

                  {/* Trust Badges */}
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Shield className="w-4 h-4 text-green-600" />
                        <span>Secure Checkout</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Truck className="w-4 h-4 text-blue-600" />
                        <span>Free Returns</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Clock className="w-4 h-4 text-orange-600" />
                        <span>Fast Delivery</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Package className="w-4 h-4 text-purple-600" />
                        <span>Quality Assured</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Checkout Button */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] z-50">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-gray-600">{itemCount} items</p>
            <p className="text-xl font-bold text-gray-900">{formatPrice(total)}</p>
          </div>
          <Button 
            onClick={handleCheckout}
            className="flex-1 max-w-xs bg-[#FFD700] hover:bg-[#FFC000] text-[#0F5132] font-bold py-4 text-lg rounded-xl"
            data-testid="checkout-button-mobile"
          >
            Checkout
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* Spacer for mobile sticky button */}
      <div className="lg:hidden h-24" />
    </div>
  );
}
